import React, { useEffect, useState, useContext } from 'react';
import { Context } from '../../context/news_search.context';
import {
  createAlert,
  createBookmarkedSearch,
} from '../../../services/bookmarked_searches/bookmarked_searches.service';
import pickBy from 'lodash/pickBy';
import { ExtendedAlert } from '../../types/news_search.types';
import './search_creation_global_styles.scss';
import classNames from 'classnames';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';

const SearchCreation: React.FC = () => {
  const {
    setDisplaySavedSearches,
    query,
    postedDate,
    authors,
    topics,
    sponsored,
    sortingField,
    setSavedSearches,
    savedSearches,
    isLoading,
    setIsLoading,
    setAlertData,
  } = useContext(Context);

  const [isConfirmationValidated, setIsConfirmationValidated] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [interval, setInterval] = useState<1 | 7>(null);

  useEffect(() => {
    setIsConfirmationValidated(
      name.trim().length &&
        !!interval &&
        !isLoading &&
        // Allow search creation only if there is at least one active filter
        (!!query ||
          !!postedDate ||
          (authors && !!authors.length) ||
          (topics && !!topics.length) ||
          sponsored === 'true')
    );
  }, [interval, name, isLoading, query, postedDate, authors, topics]);

  const onCancelClick = (): void => {
    setName('');
    setInterval(null);
    setDisplaySavedSearches(false);
  };

  const onSearchCreationConfirm = (): void => {
    setIsLoading(true);

    const params = {
      query: query ? [query] : null,
      filter: null,
      sorting: null,
    };

    if (sortingField) {
      params.sorting = {
        field: sortingField,
      };
    }

    const nonRootFilters = pickBy({
      authors: authors && authors.length ? authors : null,
      news_topics: topics && topics.length ? topics : null,
      published_since: postedDate ? [postedDate] : null,
      sponsored: sponsored === 'true' ? true : null,
    });

    if (Object.keys(nonRootFilters).length) {
      params.filter = nonRootFilters;
    }

    createBookmarkedSearch('articles', pickBy(params), name.trim())
      .then((createdSearch) => {
        createAlert(interval, createdSearch.id)
          .then((createdAlert) => {
            const extendedAlert: ExtendedAlert = {
              ...createdAlert,
              isMyAlert: true,
              periodicityDisplay: interval === 1 ? 'Daily' : 'Weekly',
            };

            const extendedSearch = {
              ...createdSearch,
              alerts: [extendedAlert],
            };

            setAlertData({
              type: 'success',
              message: `You have successfully created '${name.trim()}'.`,
            });

            setSavedSearches([...savedSearches, extendedSearch]);
          })
          .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }))
          .finally(() => {
            setName('');
            setInterval(null);
            setDisplaySavedSearches(false);
            setIsLoading(false);
          });
      })
      .catch(() => {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, });
        setName('');
        setInterval(null);
        setDisplaySavedSearches(false);
        setIsLoading(false);
      });
  };

  return (
    <div className='row'>
      <div className='col-12'>
        <p className='text-gray-li pb-1 px-1'>
          To create an alert, input keywords and filters above to define your search results and
          then select the alert&apos;s name and frequency below.
        </p>
      </div>
      <div className='col-12'>
        <p className='text-gray-li pb-2 px-1 strong'>Alert Name</p>
      </div>
      <div className='col-12 col-lg-6 pb-4'>
        <input
          type='text'
          aria-label='search-name-textbox'
          value={name}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => setName(target.value)}
          className='w-100 form-control'
        />
      </div>
      <div className='col-12 col-lg-6'>
        <div className='w-100 input-group interval-buttons-group'>
          <button
            className={classNames('btn btn-outline-info w-50', {
              'selected-interval-button': interval === 1,
            })}
            onClick={() => setInterval(1)}
          >
            <i className='fal icon-calendar-alt pe-2' />
            Daily
          </button>
          <button
            className={classNames('btn btn-outline-info w-50', {
              'selected-interval-button': interval === 7,
            })}
            onClick={() => setInterval(7)}
          >
            <i className='fal icon-calendar pe-2' />
            Weekly
          </button>
        </div>
      </div>
      <div className='col-12 mt-4'>
        <div className='d-flex flex-row-reverse align-items-baseline'>
          <button
            className='btn btn-outline-primary'
            onClick={onSearchCreationConfirm}
            disabled={!isConfirmationValidated || isLoading}
          >
            <i className='fal icon-check-circle pe-2' />
            Confirm Alert
          </button>
          <button
            className='btn btn-link link-blue-orange me-3'
            onClick={onCancelClick}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchCreation;
